import React, { useState, useEffect, useContext } from "react";
import Filter from "../../../components/glyphs/elements/filter";
import Primitives from "../../../components/primitives";
import Table from "../../../components/table-two/TableComponent";
import Button from "../../../components/button";
import { colors } from "../../../components/utils/theme";
import Pagination from "../../../components/pagination";
import Loading from "../../../components/loading";
import NoResults from "../../../components/no-results";
import { AppContext } from "../../../utils/context";
import useApiRequest from "../../../hooks/useApiRequest";
import OrderStatus from "../../../components/order-status";
import { useHistory, useParams } from "react-router";
import styled from "styled-components";
import paymentStatus from "../../../lib/payment-status";
import DownloadBtn from "../../../lib/download";
import downloadHandlerFn from "../../../components/utils/download-handler";
import FilterDateRange from "../../../lib/filters/date-range";

const StyledText = styled(Primitives.Text)`
  text-decoration: underline;
`;

const column = [
  {
    heading: "STATUS",
    type: "string"
  },
  {
    heading: "Invoice Number",
    type: "string"
  },
  {
    heading: "Invoice Date",
    type: "string"
  },
  {
    heading: "Ref.PO",
    type: "string"
  },
  {
    heading: "Ref. PO Date",
    type: "string"
  },
  {
    heading: "No. of Products",
    type: "number",
    align: "flex-end"
  },
  {
    heading: "Total Qty",
    type: "number",
    align: "flex-end"
  },
  {
    heading: "Total Free Qty",
    type: "number",
    align: "flex-end"
  },
  {
    heading: "Total Net Amt",
    type: "string",
    align: "flex-end"
  },
  {
    heading: "Total Tax",
    type: "string",
    align: "flex-end"
  },
  {
    heading: "Total Amount",
    type: "string",
    align: "flex-end"
  },
  {
    heading: "Credit Note",
    type: "string",
    align: "flex-end"
  }
];

const modifyTableData = data =>
  data.map(item => ({
    STATUS: (
      <Primitives.Flex>
        <OrderStatus
          status={
            item["status"] in paymentStatus
              ? paymentStatus[item["status"]]
              : "partially completed"
          }
        />
        <Primitives.Text ml={1}>{item["status"]}</Primitives.Text>
      </Primitives.Flex>
    ),
    "Invoice Number": item["invoice_number"],
    "Invoice Date": item["invoice_date"],
    "Ref.PO": item["po_number"],
    "Ref. PO Date": item["po_order_date"],
    "No. of Products": item["items_count"],
    "Total Qty": item["total_quantity"],
    "Total Free Qty": item["free_quantity"],
    "Total Net Amt": item["total_net_amount"],
    "Total Tax": item["total_tax_amount"],
    "Total Amount": item["total_amount"],
    "Credit Note": item["credit_note"]
  }));

const downloadHandler = async (setIsDownload, download) =>
  downloadHandlerFn(download, setIsDownload);

const InvoiceReport = () => {
  const { BASE_URL } = useContext(AppContext);
  const orderId = useParams().id;
  const [pageNumber, setPageNumber] = useState(1);
  const [request, updateRequest] = useState(
    `${BASE_URL}/payments/${orderId}?invoice_page=${pageNumber}`
  );
  const { data, isLoaded } = useApiRequest(request);
  const [dateRangeCollapse, setDateRangeCollapse] = useState(true);
  const [isDownload, setIsDownload] = useState(false);
  const [dateRanges, setDateRanges] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
      color: colors.primary[0],
      new: true
    }
  ]);

  const isClearFilter =
    dateRanges[0].startDate && dateRanges[0].endDate && !dateRanges[0].new;

  let history = useHistory();

  const dateChangeHandler = (item, collapseTheDateOptions = false) => {
    item.selection.new = false;
    setDateRanges([item.selection]);
    collapseTheDateOptions &&
      item.selection.startDate !== item.selection.endDate &&
      setDateRangeCollapse(!dateRangeCollapse);
  };

  const paginationChangeHandler = pageNumber => {
    if (pageNumber.selected) {
      setPageNumber(pageNumber.selected + 1);
    } else if (pageNumber.selected === 0) {
      setPageNumber(1);
    }
  };

  const dateRangeMapper = dateRanges => {
    if (!dateRanges[0].new)
      return (
        `&report[start_date]=${dateRanges[0].startDate
          .toLocaleDateString()
          .split("/")
          .join("-")}` +
        `&report[end_date]=${dateRanges[0].endDate
          .toLocaleDateString()
          .split("/")
          .join("-")}`
      );
  };

  useEffect(() => {
    updateRequest(`${BASE_URL}/payments/${orderId}?invoice_page=${pageNumber}`);
  }, [pageNumber]);

  const applyFilter = () => {
    updateRequest(
      `${BASE_URL}/payments/${orderId}?invoice_page=${pageNumber}${dateRangeMapper(
        dateRanges
      )}`
    );
  };

  const clearFilter = () => {
    setDateRanges([
      {
        startDate: new Date(),
        endDate: new Date(),
        key: "selection",
        color: colors.primary[0],
        new: true
      }
    ]);
    updateRequest(`${BASE_URL}/payments/${orderId}?invoice_page=${pageNumber}`);
  };

  return (
    <>
      <Primitives.Flex flexDirection="column" flex={1} px={3}>
        <Primitives.Text fontSize={4} pl={1}>
          All Invoices
        </Primitives.Text>
        <Primitives.Flex flex={1} justifyContent="space-between" py={2}>
          <Primitives.Flex>
            <Primitives.Flex width={200}>
              <FilterDateRange
                dateChangeHandler={dateChangeHandler}
                dateRangeCollapse={dateRangeCollapse}
                color={colors.text[2]}
                defaultValue={
                  dateRanges && dateRanges.selection
                    ? dateRanges.selection.startDate.toString()
                    : "Select Date"
                }
                dateRanges={dateRanges}
              />
            </Primitives.Flex>
            <Primitives.Flex px={1}>
              <Button
                active={{ backgroundColor: "inherit", color: "inherit" }}
                backgroundColor="white"
                border="1px solid"
                borderColor={colors.text[3]}
                clickHandler={applyFilter}
                glyph={<Filter height={20} width={20} fill={colors.text[1]} />}
                hover={{ backgroundColor: "inherit", color: "inherit" }}
                opacity={0.7}
                px={2}
                py={1}
              />
            </Primitives.Flex>
            <StyledText
              color={colors.text[1]}
              cursor={isClearFilter ? "pointer" : "default"}
              fontSize={1}
              lineHeight={4}
              onClick={() => {
                if (isClearFilter) {
                  // setCurrentPage(1);
                  clearFilter();
                }
              }}
              width={60}
            >
              {isClearFilter ? "Clear Filter" : ""}
            </StyledText>
          </Primitives.Flex>
          {data["invoices"] && data["invoices"].length > 0 && (
            <Primitives.Flex flex={1} justifyContent="flex-end">
              {isDownload ? (
                <Loading onlySpinner />
              ) : (
                <DownloadBtn
                  clickHandler={() => {
                    downloadHandler(
                      setIsDownload,
                      `${BASE_URL}/payments/${orderId}.xlsx?invoice_download=true${
                        dateRangeMapper(dateRanges) === undefined
                          ? ""
                          : dateRangeMapper(dateRanges)
                      }`
                    );
                  }}
                />
              )}
            </Primitives.Flex>
          )}
        </Primitives.Flex>
        {isLoaded ? (
          <Primitives.Flex flexDirection="column" mt={2}>
            <Primitives.Text color={colors.text[1]} fontSize={1} pb={3} px={1}>
              Showing {(pageNumber - 1) * 20 + 1}-{pageNumber * 20} of{" "}
              {data && data["total_invoices"]} Invoices
            </Primitives.Text>
            {data["invoices"] && data["invoices"].length ? (
              <Table
                clickHandler={(row, index) => {
                  history.push(
                    `/hospital/orders/${data["invoices"][index]["sales_order_id"]}`
                  );
                }}
                color={{ head: colors.text[1], row: colors.text[0] }}
                cursor="pointer"
                data={modifyTableData(data["invoices"])}
                column={column}
              />
            ) : (
              <NoResults />
            )}
          </Primitives.Flex>
        ) : (
          <Loading />
        )}
        {data && data["invoices"] && data["total_invoices"] > 20 && (
          <Primitives.Flex justifyContent="flex-end">
            <Pagination
              changeHandler={paginationChangeHandler}
              forcePage={pageNumber - 1}
              marginPagesDisplayed={1}
              pageCount={parseInt(data["total_invoices"]) / 20}
            />
          </Primitives.Flex>
        )}
      </Primitives.Flex>
    </>
  );
};

export default InvoiceReport;
